import { Flex } from '@chakra-ui/react'
import ApexCharts from 'apexcharts'
import { useEffect, useMemo, useRef } from 'react'
import { iIndicador, iMesPeriodo } from '../..'
import { FormatarNumero } from '../../../../../Utils/Helper'

interface BarChartProps {
  id: string
  data: iMesPeriodo[]
  indicador: iIndicador
  tipo?: 'line' | 'bar'
  mostrarPercentual?: boolean
}

export const IndicadorChart: React.FC<BarChartProps> = ({ id, data, indicador, tipo = 'line', mostrarPercentual = false }) => {
  const chartRef = useRef<ApexCharts | null>(null)

  const mesesAbreviados = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
  ]

  const dadosOrdenados = useMemo(() => {
    return [...data].sort((a, b) => a.numeroMes - b.numeroMes)
  }, [data])

  function GetAcumulado(index: number): number {
    let response = 0

    if (indicador.tipoMeta === 5) {
      return dadosOrdenados[index]?.real ?? 0
    }

    for (let i = 0; i <= index; i++) {
      response += dadosOrdenados[i]?.real ?? 0
    }

    return response
  }

  function corPonto(valor: number): string {
    return indicador.metaInversa === true && valor > indicador.meta ? '#EB5757' : '#27AE60'
  }

  useEffect(() => {
    const element = document.querySelector(`#chart-${id}`)
    if (!element) return

    if (chartRef.current) {
      chartRef.current.destroy()
      chartRef.current = null
    }

    const options = {
      chart: {
        id: indicador.nome.replaceAll(' ', '_'),
        height: 350,
        type: tipo,
        stacked: false,
        zoom: {
          enabled: false
        }
      },
      markers: {
        size: 6,
        discrete: dadosOrdenados
          .map((_, i) => ({ i, valor: GetAcumulado(i) }))
          .filter(p => indicador.metaInversa === true && p.valor > indicador.meta)
          .map(p => ({ seriesIndex: 0, dataPointIndex: p.i, fillColor: '#EB5757', strokeColor: '#EB5757', size: 6 }))
      },
      dataLabels: mostrarPercentual
        ? {
            enabled: true,
            enabledOnSeries: [0],
            offsetY: -8,
            formatter: function (val: number, opts: any) {
              const previsto = opts?.w?.globals?.series?.[1]?.[opts.dataPointIndex]
              if (!previsto) return ''
              return Math.round((val / previsto) * 100) + '%'
            },
            background: {
              enabled: false
            },
            style: {
              fontSize: '11px',
              fontWeight: 'bold',
              colors: ['#000']
            }
          }
        : {
            enabled: false
          },
      colors: ['#27AE60', '#F2994A'],
      grid: {
        padding: {
          left: 32,
          right: 32
        }
      },
      series: [
        {
          name: 'Valor Acumulado',
          data: dadosOrdenados.map((e, i) => {
            const valor = GetAcumulado(i)
            return { x: mesesAbreviados[e.numeroMes - 1], y: valor, fillColor: corPonto(valor) }
          })
        },
        {
          name: 'Valor Previsto',
          data: dadosOrdenados.map((e) => ({ x: mesesAbreviados[e.numeroMes - 1], y: e.previsto }))
        }
      ],
      stroke: {
        width: [4, 4]
      },
      plotOptions: {
        bar: {
          columnWidth: tipo === 'bar' ? '55%' : '20%'
        }
      },
      yaxis: {
        labels: {
          formatter: function (value: number) {
            if (indicador.tipoMeta === 0) {
              const semCentavos = FormatarNumero(value, indicador).split(',')[0]
              return `${indicador.medidaMeta ?? ''}${semCentavos}`
            }
            return `${FormatarNumero(value, indicador)}${indicador.medidaMeta ?? ''}`
          }
        },
        min: dadosOrdenados.some(e => (e.previsto ?? 0) < 0 || (e.real ?? 0) < 0)
          ? undefined
          : 0
      },
      tooltip: {
        shared: false,
        intersect: true,
        x: {
          show: false
        },
        y: {
          show: true,
          formatter: function (value: number) {
            return indicador.tipoMeta === 0
              ? `${indicador.medidaMeta ?? ''}${FormatarNumero(value, indicador)}`
              : `${FormatarNumero(value, indicador)}${indicador.medidaMeta ?? ''}`
          }
        }
      },
      legend: {
        horizontalAlign: 'left',
        offsetX: 40,
        itemMargin: {
          horizontal: 8,
          vertical: 4
        }
      }
    }

    const chart = new ApexCharts(element, options)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    chart.render()
    chartRef.current = chart

    return () => {
      chart.destroy()
      chartRef.current = null
    }
  }, [id, indicador, dadosOrdenados, tipo, mostrarPercentual])

  return <Flex id={`chart-${id}`} />
}
